import { Check, DescriptionOutlined } from '@mui/icons-material'
import { catalogColumnType, catalogFilterColumnType, catalogItemType, catalogTabType } from '../catalogTypes'
import { ComplianceStatus } from 'src/types'

export interface gsaItemType extends catalogItemType {
  id: string
  oemCode: string
  mfrPartNumber: string
  manufacturer: string
  previouslyApprovedClin: string | undefined
  contractCeilingPrice: number | undefined
  description: string | undefined
  awardNumber: string | undefined
  uei: string | undefined
  vendorName: string | undefined
  contractNumber: string | undefined
  sinsProposed: string | undefined
  baseProductOrAccessory: string | undefined
  vendorPartNumber: string | undefined
  upc: string | undefined
  productName: string | undefined
  productDescription: string | undefined
  uoi: string | undefined
  recycledPostConsumerMaterialContent: number | undefined
  cpl: number | undefined
  oldPrice: number | undefined
  updatedPrice: number | undefined
  discountOfferGsa: number | undefined
  gsaDiscountPriceExcludingIff: number | undefined
  gsaDiscountPriceIncludingIff: number | undefined
  coo: string | undefined
  unspsc: string | undefined
  defaultPhoto: string | undefined
  quantityPerPack: string | undefined
  quantityUnitUom: string | undefined
  deliveryDays: string | undefined
  leadTimeCode: string | undefined
  isMiaRisk: boolean | undefined
  isTaaRisk: boolean | undefined
  deliveryDate: string | undefined
  contractVehicle: string
  contractName: string | undefined
  dateAdded: Date
  sourceImpresQuoteNumber: string | undefined
  modType: string | undefined
  prodType: string | undefined
  modDate: Date
  status: 'inProgress' | 'processing' | 'completed' | 'failed' | undefined
  uploadedFile: string | undefined
  gsaCtaCatalog: string | undefined
  uploadedDate: Date
  errorMessage: string | undefined
  discountPricetoBpa: number | undefined //BPA contract ceiling price
  percentDiscountBpa: number | undefined
  samUei: string | undefined
  duns: string | undefined
  catalogDate: Date
  addProductDescription: string | undefined
  msrp: number | undefined
  gsaPrice: number | undefined
  unitPrice2GitBpa: number | undefined
  warranty: string | undefined
  category: string | undefined
  sku: string | undefined
  gsaStatus: string | undefined
  pl: string | undefined
  notes: string | undefined
  gsaNteIncludingIff: number | undefined
  gitBpa: number | undefined
  bpa: string | undefined
  osts: string | undefined
  tdsnxPartNumber: string | undefined
  'addMod#': string | undefined
  olmCeilingPrice: number | undefined
}

const ALL_MAS_COLUMNS: catalogColumnType[] = [
  { key: 'awardNumber', label: 'Award / Mod No.', width: '100px' },
  { key: 'uei', label: 'UEI', width: '150px' },
  { key: 'vendorName', label: 'Vendor Name', width: '150px' },
  { key: 'contractNumber', label: 'Contract / Offer No.', width: '100px' },
  { key: 'sinsProposed', label: 'SIN(s)', width: '100px' },
  { key: 'baseProductOrAccessory', label: 'Base or Accessory', width: '100px' },
  { key: 'manufacturer', label: 'Manufacturer Name', width: '250px' },
  { key: 'mfrPartNumber', label: 'MFR Part No.', width: '100px' },
  { key: 'vendorPartNumber', label: 'Vendor Part No.', width: '100px' },
  { key: 'upc', label: 'UPC', width: '100px' },
  { key: 'productName', label: 'Product Name', width: '150px' },
  { key: 'description', label: 'Product Description', width: '300px' },
  { key: 'uoi', label: 'UOI', width: '100px' },
  {
    key: 'cpl',
    label: 'Commercial List Price (CPL)',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  {
    key: 'discountOfferGsa',
    label: 'Discount to GSA (Excl. IFF) (%)',
    width: '150px',
    inputType: 'percentage',
    align: 'right',
  },
  {
    key: 'gsaDiscountPriceExcludingIff',
    label: 'Discount Price to GSA (Excl. IFF)',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  {
    key: 'gsaDiscountPriceIncludingIff',
    label: 'Discount Price to GSA (Incl. IFF)',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  { key: 'coo', label: 'COO', width: '100px' },
  { key: 'unspsc', label: 'UNSPSC', width: '100px', defaultHidden: true },
  { key: 'defaultPhoto', label: 'Default Photo', width: '150px', defaultHidden: true },
  { key: 'quantityPerPack', label: 'Quantity Per Pack', width: '150px', defaultHidden: true },
  { key: 'quantityUnitUom', label: 'Quantity Unit UOM', width: '150px', defaultHidden: true },
  { key: 'deliveryDays', label: 'Delivery Days', width: '150px', defaultHidden: true },
  { key: 'leadTimeCode', label: 'Lead Time Code', width: '150px', defaultHidden: true },
]

const ALL_OPEN_MARKET_COLUMNS: catalogColumnType[] = [
  { key: 'contractVehicle', label: 'Contract Vehicle', width: '100px' },
  { key: 'contractNumber', label: 'Contract No.', width: '150px' },
  { key: 'contractName', label: 'Contract Name', width: '150px' },
  {
    key: 'dateAdded',
    label: 'Date Added',
    width: '100px',
    inputType: 'date',
  },
  { key: 'sourceImpresQuoteNumber', label: 'Source IMPRES Quote No.', width: '100px' },
  { key: 'manufacturer', label: 'Manufacturer Name', width: '250px' },
  { key: 'mfrPartNumber', label: 'MFR Part No.', width: '100px' },
]

const ALL_OLM_COLUMNS: catalogColumnType[] = [
  { key: 'contractVehicle', label: 'Contract Vehicle', width: '100px' },
  { key: 'contractNumber', label: 'Contract No.', width: '150px' },
  { key: 'contractName', label: 'Contract Name', width: '150px' },
  {
    key: 'dateAdded',
    label: 'Date Added to OLM',
    width: '100px',
    inputType: 'date',
  },
  { key: 'sourceImpresQuoteNumber', label: 'Source IMPRES Quote No.', width: '100px' },
  { key: 'manufacturer', label: 'Manufacturer Name', width: '250px' },
  { key: 'mfrPartNumber', label: 'MFR Part No.', width: '100px' },
  {
    key: 'olmCeilingPrice',
    label: 'Olm Ceiling Price',
    width: '100px',
    inputType: 'currency',
    align: 'right',
  },
  { key: 'coo', label: 'Country of Origin', width: '100px' },
  { key: 'taa', label: 'TAA', width: '100px' },
]

const ALL_CTA_COLUMNS: catalogColumnType[] = [
  { key: 'sinsProposed', label: 'SIN', width: '100px' },
  { key: 'manufacturer', label: 'Manufacturer', width: '250px' },
  { key: 'mfrPartNumber', label: 'MFR Part No.', width: '150px' },
  { key: 'description', label: 'Description', width: '300px' },
  {
    key: 'listPrice',
    label: 'List Price',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  {
    key: 'catalogPrice',
    label: 'Catalog Price',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  { key: 'warranty', label: 'Warranty', width: '150px' },
  { key: 'deliveryTime', label: 'Delivery Time', width: '150px' },
  { key: 'transactionType', label: 'Transaction Type', width: '150px' },
  { key: 'productGroup', label: 'Product Group', width: '150px' },
]

const ALL_BPA_COLUMNS: catalogColumnType[] = [
  ...ALL_MAS_COLUMNS.slice(0, 17),
  {
    key: 'discountPricetoBpa',
    label: 'Discount Price to BPA',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  {
    key: 'percentDiscountBpa',
    label: '% Discount to BPA',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  ...ALL_MAS_COLUMNS.slice(17),
]

//GS-35F-0563U
const WESTCON_TDSYNNEX_COLUMNS: catalogColumnType[] = [
  { key: 'samUei', label: 'SAM UEI', width: '150px' },
  { key: 'duns', label: 'DUNS', width: '150px' },
  { key: 'vendorName', label: 'Vendor Name', width: '150px' },
  { key: 'sinsProposed', label: 'SIN', width: '100px' },
  { key: 'catalogDate', label: 'Catalog Date', width: '150px' },
  { key: 'manufacturer', label: 'Manufacturer', width: '250px' },
  { key: 'mfrPartNumber', label: 'MFR Part No.', width: '150px' },
  { key: 'description', label: 'Description', width: '300px' },
  { key: 'addProductDescription', label: 'Additional Product Description', width: '300px' },
  {
    key: 'msrp',
    label: 'MSRP',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  {
    key: 'gsaPrice',
    label: 'GSA Price',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  { key: 'warranty', label: 'Warranty', width: '150px' },
  { key: 'category', label: 'Category', width: '150px' },
]

//47QTCA19D00MM
const TD_SYNNEX_CTA_COLUMNS: catalogColumnType[] = [
  { key: 'sinsProposed', label: 'SIN', width: '100px' },
  { key: 'manufacturer', label: 'Manufacturer', width: '250px' },
  { key: 'mfrPartNumber', label: 'MFR Part No.', width: '150px' },
  { key: 'productName', label: 'Product Name', width: '300px' },
  { key: 'tdsnxPartNumber', label: 'TDSNX Part Number', width: '150px' },
  { key: 'sku', label: 'SKU', width: '150px' },
  { key: 'gsaStatus', label: 'GSA Status', width: '150px' },
  { key: 'pl', label: 'PL', width: '150px' },
  { key: 'notes', label: 'Notes', width: '150px' },
  { key: 'description', label: 'Description', width: '300px' },
  {
    key: 'msrp',
    label: 'GSA MSRP',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  {
    key: 'gsaNteIncludingIff',
    label: 'GSA NTE (Incl. IFF)',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  {
    key: 'gitBpa',
    label: '2GIT BPA',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  { key: 'upc', label: 'UPC', width: '150px' },
  { key: 'bpa', label: 'BPA', width: '150px' },
  { key: 'osts', label: 'OSTS', width: '150px' },
  { key: 'addMod#', label: 'ADDMod#', width: '150px' },
]

const modDateColumn = (width: string): catalogColumnType => ({
  key: 'modDate',
  label: 'MOD Date',
  width,
  inputType: 'date',
})

const priceChangeColumns = (width: string): catalogColumnType[] => [
  {
    key: 'oldPrice',
    label: 'Old Price',
    width,
    inputType: 'currency',
  },
  {
    key: 'updatedPrice',
    label: 'Updated Price',
    width,
    inputType: 'currency',
  },
]

const BPA_CONTRACTS = [
  '86615424f00010', //HUD Software
  '1331L525A13OS0016', //DPA DOC NETS
  '86615424A00001', //HUD HW & UC
  '12314425A0007', //USDA Laptop
  '12314425A0003', //USDA Tablet
  '1605TB22A0001', //DOL BPA
  'GS-35F-0002V-GSS', //GSA - GSS
]

const bpaColumnEntries = (contract: string): [string, catalogColumnType[]][] => [
  [contract, ALL_BPA_COLUMNS],
  [
    `${contract}:Modifications`,
    [
      { key: 'awardNumber', label: 'Award / Mod No.', width: '150px' },
      { key: 'modType', label: 'MOD Type', width: '75px' },
      modDateColumn('75px'),
      { key: 'status', label: 'Status', width: '50px' },
      { key: 'uploadedFile', label: 'Uploaded File', width: '150px' },
    ],
  ],
  [`${contract}:MOD Report | Item Additions`, [modDateColumn('150px'), ...ALL_BPA_COLUMNS]],
  [`${contract}:MOD Report | Item Deletions`, [modDateColumn('150px'), ...ALL_BPA_COLUMNS]],
  [
    `${contract}:MOD Report | Price Increase`,
    [modDateColumn('150px'), ...priceChangeColumns('150px'), ...ALL_BPA_COLUMNS],
  ],
  [
    `${contract}:MOD Report | Price Decrease`,
    [modDateColumn('150px'), ...priceChangeColumns('150px'), ...ALL_BPA_COLUMNS],
  ],
]

const bpaTabGroup = (contract: string, label: string = contract.toUpperCase()): [string, catalogTabType[]] => [
  contract,
  [
    { label, icon: <DescriptionOutlined />, source: contract },
    { label: 'Modifications', source: `${contract}/modifications`, isModification: true },
    { label: 'MOD Report | Price Increase', source: `${contract}/audit`, modType: 'Price Increase' },
    { label: 'MOD Report | Price Decrease', source: `${contract}/audit`, modType: 'Price Decrease' },
    { label: 'MOD Report | Item Additions', source: `${contract}/audit`, modType: 'Additions' },
    { label: 'MOD Report | Item Deletions', source: `${contract}/audit`, modType: 'Deletions' },
  ],
]

export const gsaColumnMapping = new Map<string, catalogColumnType[]>([
  ['gs-35f-0002v', ALL_MAS_COLUMNS],
  [
    'gs-35f-0002v:Modifications',
    [
      { key: 'awardNumber', label: 'Award / Mod No.', width: '150px' },
      { key: 'modType', label: 'MOD Type', width: '75px' },
      modDateColumn('75px'),
      { key: 'status', label: 'Status', width: '50px' },
      { key: 'uploadedFile', label: 'Uploaded File', width: '150px' },
    ],
  ],
  ['gs-35f-0002v:MOD Report | Item Additions', [modDateColumn('150px'), ...ALL_MAS_COLUMNS]],
  ['gs-35f-0002v:MOD Report | Item Deletions', [modDateColumn('150px'), ...ALL_MAS_COLUMNS]],
  [
    'gs-35f-0002v:MOD Report | Price Increase',
    [modDateColumn('150px'), ...priceChangeColumns('150px'), ...ALL_MAS_COLUMNS],
  ],
  [
    'gs-35f-0002v:MOD Report | Price Decrease',
    [modDateColumn('150px'), ...priceChangeColumns('150px'), ...ALL_MAS_COLUMNS],
  ],
  ['gs-35f-0002v-open-market', ALL_OPEN_MARKET_COLUMNS],
  [
    'gs-35f-0002v-open-market:Line Approval',
    [
      { key: 'check' as keyof gsaItemType, label: '', width: '48px' },
      { key: 'approvalStatus' as keyof gsaItemType, label: 'Status', width: '100px' },
      ...ALL_OPEN_MARKET_COLUMNS,
    ],
  ],
  ['gs-35f-0002v-olm', ALL_OLM_COLUMNS],
  [
    'gs-35f-0002v-olm:Modifications',
    [
      { key: 'awardNumber', label: 'Award / Mod No.', width: '150px' },
      { key: 'modType', label: 'MOD Type', width: '75px' },
      modDateColumn('75px'),
      { key: 'status', label: 'Status', width: '50px' },
      { key: 'uploadedFile', label: 'Uploaded File', width: '150px' },
    ],
  ],
  [
    'gs-35f-0002v-olm:Line Approval',
    [
      { key: 'check' as keyof gsaItemType, label: '', width: '48px' },
      { key: 'approvalStatus' as keyof gsaItemType, label: 'Status', width: '100px' },
      ...ALL_OLM_COLUMNS,
    ],
  ],
  ['gs-35f-0511t', ALL_CTA_COLUMNS],
  [
    'gs-35f-0511t:Modifications',
    [
      {
        key: 'uploadedDate',
        label: 'MOD Date',
        width: '75px',
        inputType: 'date',
      },
      { key: 'status', label: 'Status', width: '50px' },
      { key: 'uploadedFile', label: 'Uploaded File', width: '150px' },
    ],
  ],
  [
    'gs-35f-0511t:MOD Report | Price Increase',
    [modDateColumn('75px'), ...priceChangeColumns('75px'), ...ALL_CTA_COLUMNS],
  ],
  [
    'gs-35f-0511t:MOD Report | Price Decrease',
    [modDateColumn('75px'), ...priceChangeColumns('75px'), ...ALL_CTA_COLUMNS],
  ],
  ['gs-35f-0511t:MOD Report | Item Additions', [modDateColumn('75px'), ...ALL_CTA_COLUMNS]],
  ['gs-35f-0511t:MOD Report | Item Deletions', [modDateColumn('75px'), ...ALL_CTA_COLUMNS]],
  ['47qtca23d00b9', ALL_CTA_COLUMNS],
  [
    '47qtca23d00b9:Modifications',
    [
      { key: 'modType', label: 'MOD Type', width: '75px' },
      modDateColumn('75px'),
      { key: 'status', label: 'Status', width: '50px' },
      { key: 'uploadedFile', label: 'Uploaded File', width: '150px' },
    ],
  ],
  [
    '47qtca23d00b9:MOD Report | Price Increase',
    [modDateColumn('75px'), ...priceChangeColumns('75px'), ...ALL_CTA_COLUMNS],
  ],
  [
    '47qtca23d00b9:MOD Report | Price Decrease',
    [modDateColumn('75px'), ...priceChangeColumns('75px'), ...ALL_CTA_COLUMNS],
  ],
  ['47qtca23d00b9:MOD Report | Item Additions', [modDateColumn('75px'), ...ALL_CTA_COLUMNS]],
  ['47qtca23d00b9:MOD Report | Item Deletions', [modDateColumn('75px'), ...ALL_CTA_COLUMNS]],
  ...BPA_CONTRACTS.flatMap(bpaColumnEntries),
  ['86615424A00001', ALL_BPA_COLUMNS],
  ['gs-35f-0002v-gss', ALL_MAS_COLUMNS],
  ['gs-35f-0563u', WESTCON_TDSYNNEX_COLUMNS],
  [
    'gs-35f-0563u:Modifications',
    [
      { key: 'modType', label: 'MOD Type', width: '75px' },
      modDateColumn('75px'),
      { key: 'status', label: 'Status', width: '50px' },
      { key: 'uploadedFile', label: 'Uploaded File', width: '150px' },
    ],
  ],
  [
    'gs-35f-0563u:MOD Report | Price Increase',
    [modDateColumn('75px'), ...priceChangeColumns('75px'), ...WESTCON_TDSYNNEX_COLUMNS],
  ],
  [
    'gs-35f-0563u:MOD Report | Price Decrease',
    [modDateColumn('75px'), ...priceChangeColumns('75px'), ...WESTCON_TDSYNNEX_COLUMNS],
  ],
  ['gs-35f-0563u:MOD Report | Item Additions', [modDateColumn('75px'), ...WESTCON_TDSYNNEX_COLUMNS]],
  ['gs-35f-0563u:MOD Report | Item Deletions', [modDateColumn('75px'), ...WESTCON_TDSYNNEX_COLUMNS]],
  ['47qtca19d00mm', TD_SYNNEX_CTA_COLUMNS],
  [
    '47qtca19d00mm:Modifications',
    [
      { key: 'modType', label: 'MOD Type', width: '75px' },
      modDateColumn('75px'),
      { key: 'status', label: 'Status', width: '50px' },
      { key: 'uploadedFile', label: 'Uploaded File', width: '150px' },
    ],
  ],
  [
    '47qtca19d00mm:MOD Report | Price Increase',
    [modDateColumn('75px'), ...priceChangeColumns('75px'), ...TD_SYNNEX_CTA_COLUMNS],
  ],
  [
    '47qtca19d00mm:MOD Report | Price Decrease',
    [modDateColumn('75px'), ...priceChangeColumns('75px'), ...TD_SYNNEX_CTA_COLUMNS],
  ],
  ['47qtca19d00mm:MOD Report | Item Additions', [modDateColumn('75px'), ...TD_SYNNEX_CTA_COLUMNS]],
  ['47qtca19d00mm:MOD Report | Item Deletions', [modDateColumn('75px'), ...TD_SYNNEX_CTA_COLUMNS]],
])

export const gsaTabGroups = new Map<string, catalogTabType[]>([
  [
    'gs-35f-0002v',
    [
      { label: 'GS-35F-0002V', icon: <DescriptionOutlined />, source: 'gs-35f-0002v' },
      { label: 'Modifications', icon: <Check />, source: 'gs-35f-0002v/modifications', isModification: true },
      { label: 'MOD Report | Price Increase', source: 'gs-35f-0002v/audit', modType: 'Price Increase' },
      { label: 'MOD Report | Price Decrease', source: 'gs-35f-0002v/audit', modType: 'Price Decrease' },
      { label: 'MOD Report | Item Additions', source: 'gs-35f-0002v/audit', modType: 'Additions' },
      { label: 'MOD Report | Item Deletions', source: 'gs-35f-0002v/audit', modType: 'Deletions' },
    ],
  ],
  [
    'gs-35f-0002v-olm',
    [
      {
        label: 'GS-35F-0002V-OLM',
        icon: <DescriptionOutlined />,
        source: 'gs-35f-0002v-olm',
        filters: { approvalStatus: ComplianceStatus.Compliant },
      },
      {
        label: 'Modifications',
        source: 'gs-35f-0002v-olm/modifications',
        isModification: true,
      },
      {
        label: 'Line Approval',
        icon: <Check />,
        source: 'gs-35f-0002v-olm',
        filters: { approvalStatus: ComplianceStatus.PendingApproval },
      },
    ],
  ],
  [
    'gs-35f-0002v-open-market',
    [
      {
        label: 'GS-35F-0002V-Open-Market',
        icon: <DescriptionOutlined />,
        source: 'gs-35f-0002v-open-market',
        filters: { approvalStatus: ComplianceStatus.Compliant },
      },
      {
        label: 'Line Approval',
        icon: <Check />,
        source: 'gs-35f-0002v-open-market',
        filters: { approvalStatus: ComplianceStatus.PendingApproval },
      },
    ],
  ],
  [
    'gs-35f-0511t',
    [
      { label: 'GS-35F-0511T', icon: <DescriptionOutlined />, source: 'gs-35f-0511t' },
      { label: 'Modifications', source: 'gs-35f-0511t/modifications', isModification: true },
      { label: 'MOD Report | Price Increase', source: 'gs-35f-0511t/audit', modType: 'Price Increase' },
      { label: 'MOD Report | Price Decrease', source: 'gs-35f-0511t/audit', modType: 'Price Decrease' },
      { label: 'MOD Report | Item Additions', source: 'gs-35f-0511t/audit', modType: 'Additions' },
      { label: 'MOD Report | Item Deletions', source: 'gs-35f-0511t/audit', modType: 'Deletions' },
    ],
  ],
  //CTA
  [
    '47qtca23d00b9',
    [
      { label: '47QTCA23D00B9', icon: <DescriptionOutlined />, source: '47qtca23d00b9' },
      { label: 'Modifications', source: '47qtca23d00b9/modifications', isModification: true },
      { label: 'MOD Report | Price Increase', source: 'gs-35f-0511t/audit', modType: 'Price Increase' },
      { label: 'MOD Report | Price Decrease', source: 'gs-35f-0511t/audit', modType: 'Price Decrease' },
      { label: 'MOD Report | Item Additions', source: 'gs-35f-0511t/audit', modType: 'Additions' },
      { label: 'MOD Report | Item Deletions', source: 'gs-35f-0511t/audit', modType: 'Deletions' },
    ],
  ],
  [
    'gs-35f-0563u',
    [
      { label: 'GS-35F-0563U', icon: <DescriptionOutlined />, source: 'gs-35f-0563u' },
      { label: 'Modifications', source: 'gs-35f-0563u/modifications', isModification: true },
      { label: 'MOD Report | Price Increase', source: 'gs-35f-0563u/audit', modType: 'Price Increase' },
      { label: 'MOD Report | Price Decrease', source: 'gs-35f-0563u/audit', modType: 'Price Decrease' },
      { label: 'MOD Report | Item Additions', source: 'gs-35f-0563u/audit', modType: 'Additions' },
      { label: 'MOD Report | Item Deletions', source: 'gs-35f-0563u/audit', modType: 'Deletions' },
    ],
  ],
  [
    '47qtca19d00mm',
    [
      { label: '47QTCA19D00MM', icon: <DescriptionOutlined />, source: '47qtca19d00mm' },
      { label: 'Modifications', source: '47qtca19d00mm/modifications', isModification: true },
      { label: 'MOD Report | Price Increase', source: '47qtca19d00mm/audit', modType: 'Price Increase' },
      { label: 'MOD Report | Price Decrease', source: '47qtca19d00mm/audit', modType: 'Price Decrease' },
      { label: 'MOD Report | Item Additions', source: '47qtca19d00mm/audit', modType: 'Additions' },
      { label: 'MOD Report | Item Deletions', source: '47qtca19d00mm/audit', modType: 'Deletions' },
    ],
  ],
  ...BPA_CONTRACTS.map(contract => bpaTabGroup(contract)),
])

const shareFilter = (keys: string[], cols: catalogFilterColumnType[]) =>
  keys.map(k => [k, cols] as [string, catalogFilterColumnType[]])

const OLM_OPEN_MARKET_FILTERS: catalogFilterColumnType[] = [
  { key: 'mfrPartNumber', label: 'MFR Part No.' },
  { key: 'manufacturer', label: 'Manufacturer Name' },
  { key: 'dateAdded', label: 'Date Added', type: 'date' },
  { key: 'sourceImpresQuoteNumber', label: 'Source IMPRES Quote No.' },
]

export const gsaColumnFilter = new Map<string, catalogFilterColumnType[]>([
  [
    'gs-35f-0002v',
    [
      { key: 'awardNumber', label: 'Award / Mod No.' },
      { key: 'vendorName', label: 'Vendor Name' },
      { key: 'manufacturer', label: 'Manufacturer Name' },
      { key: 'mfrPartNumber', label: 'MFR Part No.' },
      { key: 'description', label: 'Product Description' },
      { key: 'contractNumber', label: 'Contract / Offer No.' },
      { key: 'vendorPartNumber', label: 'Vendor Part No.' },
      { key: 'uei', label: 'UEI' },
      { key: 'baseProductOrAccessory', label: 'Base or Accessory' },
      { key: 'productName', label: 'Product Name' },
    ],
  ],
  [
    'gs-35f-0002v:Modifications',
    [
      { key: 'modNumber', label: 'MOD #' },
      { key: 'modType', label: 'MOD Type' },
      { key: 'modDate', label: 'MOD Date', type: 'date' },
    ],
  ],
  ...shareFilter(
    [
      'gs-35f-0002v:MOD Report | Item Additions',
      'gs-35f-0002v:MOD Report | Item Deletions',
      'gs-35f-0002v:MOD Report | Price Increase',
      'gs-35f-0002v:MOD Report | Price Decrease',
    ],
    [
      { key: 'manufacturer', label: 'Manufacturer Name' },
      { key: 'mfrPartNumber', label: 'MFR Part No.' },
      { key: 'awardNumber', label: 'Award / Mod No.' },
      { key: 'modDate', label: 'MOD Date', type: 'date' },
    ],
  ),
  ...shareFilter(['gs-35f-0002v-olm', 'gs-35f-0002v-open-market'], OLM_OPEN_MARKET_FILTERS),
  ...shareFilter(
    ['gs-35f-0002v-olm:Line Approval', 'gs-35f-0002v-open-market:Line Approval'],
    [{ key: 'status', label: 'Status' }, ...OLM_OPEN_MARKET_FILTERS],
  ),
  ['cta:CTA Modifications', [{ key: 'uploadedDate', label: 'Uploaded Date', type: 'date' }]],
])
